import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import clsx from 'clsx'
import { useTreeViewStyles } from 'components/WorkspaceEditor/styles'
import React, { memo, useEffect } from 'react'
import type { ContentEditorProps } from '../../ContentEditor'
import { withLazyLoad } from '../../withLazyComponent'
import { usePlainTextController } from './usePlainTextController'

/** Props for the PlainText component. */
export interface PlainTextProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The classes to apply the styling */
    classes?: string
}

const ContentEditor = withLazyLoad<ContentEditorProps>(() =>
    import('../../ContentEditor').then((module) => ({
        default: module.ContentEditor,
    }))
)

/** Props for the KeyedText component. */
interface KeyedTextProps {
    /** The keyed text value (typically a label followed by colon) */
    keyedText: string
    /** The class name to apply styling */
    classes?: string
    /** Optional test id for testing */
    'data-testid'?: string
}

/**
 * A component that renders a keyed text.
 * @param {KeyedTextProps} props - Props for the component.
 */
const KeyedText = memo<KeyedTextProps>(
    ({ keyedText, classes, 'data-testid': dataTestId }) => {
        const { readOnlyCursor } = useTreeViewStyles()
        return (
            <Typography
                variant="caption"
                className={clsx(classes, readOnlyCursor)}
                data-testid={dataTestId || 'keyed-text'}
            >
                {keyedText}
            </Typography>
        )
    }
)

/** Props for the text editor component */
interface EditorContentProps extends ContentEditorProps {
    /** Whether this text has a label prefix (like "Title:") */
    isKeyedText: boolean
    /** The label text that appears before the editable portion */
    keyedText: string
    /** Function to set the editing state */
    setIsEditingPlainText: (value: boolean) => void
    /** The editable text portion after the label */
    keyedTextValue?: string
    /** CSS styling classes */
    classes?: string
}

/**
 * A text editor that handles two display formats:
 * 1. Regular text editing (standard mode)
 * 2. Label-value pairs where the label (with semicolon) is fixed and only the value is editable
 *
 * Content team members always see the standard editor regardless of format.
 *
 * @param {EditorContentProps} props - Props for the component.
 */
const EditorContent = memo<EditorContentProps>(
    ({
        initialData,
        initialSuggestions,
        handlers,
        setIsEditingPlainText,
        isKeyedText,
        keyedText,
        keyedTextValue,
        classes,
    }) => {
        useEffect(() => {
            setIsEditingPlainText(true)

            return () => {
                setIsEditingPlainText(false)
            }
        }, [setIsEditingPlainText])

        const shouldUseStandardEditor =
            !isKeyedText || isReactiveContentTeamFromStudioPage()?.value

        const editorComponent = (
            <ContentEditor
                initialData={initialData}
                initialSuggestions={initialSuggestions}
                handlers={handlers}
            />
        )

        // show standard editor, for regular text or from OM/SMM content
        if (shouldUseStandardEditor) {
            return editorComponent
        }

        // show label separately from editable value, for keyed text
        return (
            <>
                <KeyedText
                    keyedText={keyedText}
                    classes={classes}
                    data-testid="keyed-text-key"
                />
                {keyedTextValue && editorComponent}
            </>
        )
    }
)

/**
 * An editable component that renders plain text. It uses Typography (caption variant) from Material-UI as a placeholder for CKEditor (ContentEditor).
 * Can be editable based on:
 * - by default, the component is not editable
 * - when the component has changes (initial suggestions)
 * - when focused or clicked
 * @param {PlainTextProps} props - Props for the component.
 */
export const PlainText = memo<PlainTextProps>(
    ({ contentItem, inactive = false, editable = false, classes }) => {
        /** controller props and handlers */
        const {
            text,
            keyedText,
            keyedTextValue,
            formattedContentId,
            initialData,
            initialSuggestions,
            ckeToggle,
            shouldRenderContentEditor,
            isKeyedText,
            handlers,
            setIsEditingPlainText,
        } = usePlainTextController(contentItem)

        const isEditMode = shouldRenderContentEditor && !inactive && editable

        return (
            <Typography
                variant="caption"
                key={formattedContentId}
                data-testid={formattedContentId}
                data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                className={classes}
                tabIndex={0}
                ref={ckeToggle.active ? ckeToggle.placeholderRef : null}
                onKeyDown={handlers.onKeyDown}
                onFocusCapture={handlers.onFocusCapture}
            >
                {isEditMode ? (
                    <EditorContent
                        initialData={initialData}
                        initialSuggestions={initialSuggestions}
                        handlers={handlers}
                        setIsEditingPlainText={setIsEditingPlainText}
                        isKeyedText={isKeyedText}
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        classes={classes}
                    />
                ) : (
                    text
                )}
            </Typography>
        )
    }
)
